import React from 'react';
import { Table } from 'antd';
import { getElement } from '../utils';
import { getTenGod, HeavenlyStem } from '../utils/tenGod';

interface FourPillarsProps {
  year: string;
  month: string;
  day: string;
  hour: string;
  locale?: 'zh-CN' | 'en-US';
}

export const getColor = (element: string): string => {
  switch (element) {
    case '金': return '#DAA520';
    case '木': return '#228B22';
    case '水': return '#1E90FF';
    case '火': return '#FF4500';
    case '土': return '#CD853F';
    default: return 'black';
  }
};

const getHiddenStems = (earthlyBranch: string): string[] => {
  const hiddenStemsMap: { [key: string]: string[] } = {
    '子': ['癸'],
    '丑': ['己', '癸', '辛'],
    '寅': ['甲', '丙', '戊'],
    '卯': ['乙'],
    '辰': ['戊', '乙', '癸'],
    '巳': ['丙', '庚', '戊'],
    '午': ['丁', '己'],
    '未': ['己', '丁', '乙'],
    '申': ['庚', '壬', '戊'],
    '酉': ['辛'],
    '戌': ['戊', '辛', '丁'],
    '亥': ['壬', '甲'],
  };
  return hiddenStemsMap[earthlyBranch] || [];
};

const PILLAR_COPY = {
  'zh-CN': {
    columns: ['年柱', '月柱', '日柱', '时柱'],
    tenGodStem: '天干十神',
    stem: '天干',
    branch: '地支',
    hiddenStems: '藏干',
    branchTenGod: '地支十神',
  },
  'en-US': {
    columns: ['Year Pillar', 'Month Pillar', 'Day Pillar', 'Hour Pillar'],
    tenGodStem: 'Stem God',
    stem: 'Stem',
    branch: 'Branch',
    hiddenStems: 'Hidden Stems',
    branchTenGod: 'Branch Gods',
  },
} as const;

const FourPillars: React.FC<FourPillarsProps> = ({ year, month, day, hour, locale = 'zh-CN' }) => {
  const pillars = [year, month, day, hour];
  const dayMaster = day[0] as HeavenlyStem;
  const labels = PILLAR_COPY[locale];
  const isUnknownPillar = (pillar: string) => pillar === '**';
  const getPillarStem = (pillar: string) => (isUnknownPillar(pillar) ? '*' : pillar[0]);
  const getPillarBranch = (pillar: string) => (isUnknownPillar(pillar) ? '*' : pillar[1]);

  const columns = labels.columns.map(title => ({
    title,
    dataIndex: title,
    key: title,
    align: 'center' as const,
  }));

  const stemBranchStyle = {
    fontSize: '20px',
    fontWeight: 600,
  };

  const data = [
    {
      key: labels.tenGodStem,
      ...Object.fromEntries(pillars.map((pillar, index) => [
        columns[index].title,
        <span style={{ fontSize: '12px' }}>
          {isUnknownPillar(pillar) ? '--' : getTenGod(dayMaster, pillar[0])}
        </span>

      ]))
    },
    {
      key: labels.stem,
      ...Object.fromEntries(pillars.map((pillar, index) => [
        columns[index].title,
        <span style={{ ...stemBranchStyle, color: isUnknownPillar(pillar) ? '#999' : getColor(getElement(pillar[0])) }}>
          {getPillarStem(pillar)}
        </span>

      ]))
    },
    {
      key: labels.branch,
      ...Object.fromEntries(pillars.map((pillar, index) => [
        columns[index].title,
        <span style={{ ...stemBranchStyle, color: isUnknownPillar(pillar) ? '#999' : getColor(getElement(pillar[1])) }}>
          {getPillarBranch(pillar)}
        </span>
      ]))
    },
    {
      key: labels.hiddenStems,
      ...Object.fromEntries(pillars.map((pillar, index) => [
        columns[index].title,
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {isUnknownPillar(pillar) ? <span style={{ color: '#999' }}>--</span> : getHiddenStems(pillar[1]).map((stem, i) => (
            <span key={i} style={{ color: getColor(getElement(stem)) }}>
              {stem}({getElement(stem as HeavenlyStem)})
            </span>
          ))}
        </div>
      ]))
    },
    {
      key: labels.branchTenGod,
      ...Object.fromEntries(pillars.map((pillar, index) => [
        columns[index].title,
        <div style={{ display: 'flex', fontSize: '12px', flexDirection: 'column', alignItems: 'center' }}>
          {isUnknownPillar(pillar) ? <span>--</span> : getHiddenStems(pillar[1]).map((stem, i) => (
            <span key={i}>
              {getTenGod(dayMaster, stem)}
            </span>
          ))}
        </div>
      ]))
    },
  ];

  return (
    <div className="space-y-4">
      <div style={{
        overflow: 'auto',
        borderRadius: '16px',
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(212, 175, 106, 0.2)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)'
      }}>
        <Table 
          columns={columns} 
          dataSource={data} 
          pagination={false}
          style={{ background: 'transparent' }}
        />
      </div>
      {/* <div style={{ 
        marginTop: '20px',
        padding: '24px',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(212, 175, 106, 0.08) 100%)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: '1px solid rgba(212, 175, 106, 0.2)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, #667eea, #764ba2, #d4af6a)',
          backgroundSize: '200% auto',
          animation: 'borderFlow 3s linear infinite'
        }} />
        <div style={{ 
          fontSize: '16px',
          fontWeight: 600,
          background: 'linear-gradient(135deg, #667eea, #764ba2, #d4af6a)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '16px'
        }}>
          你的 MBTI 可能是：<strong style={{ fontSize: '20px' }}>{mbtiType}</strong>
        </div>
        <div style={{ marginTop: '16px', fontSize: '14px', color: '#555' }}>
          <div style={{ fontWeight: 600, marginBottom: '12px', color: '#333' }}>推断依据：</div>
          <div style={{ marginTop: '8px' }}>
            <div style={{ 
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '12px',
              marginBottom: '12px',
              border: '1px solid rgba(212, 175, 106, 0.15)'
            }}>
              <div style={{ fontWeight: 600, color: '#667eea', marginBottom: '8px' }}>E/I 维度：{mbtiType[0]}</div>
              <ul style={{ marginLeft: '20px', marginTop: '6px', lineHeight: '1.6' }}>
                {reasoning.ei.map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#888' }}>倾向：<span style={{ color: '#667eea' }}>{trends.ei}</span></div>
            </div>
            <div style={{ 
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '12px',
              marginBottom: '12px',
              border: '1px solid rgba(212, 175, 106, 0.15)'
            }}>
              <div style={{ fontWeight: 600, color: '#764ba2', marginBottom: '8px' }}>S/N 维度：{mbtiType[1]}</div>
              <ul style={{ marginLeft: '20px', marginTop: '6px', lineHeight: '1.6' }}>
                {reasoning.sn.map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
            </div>
            <div style={{ 
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '12px',
              marginBottom: '12px',
              border: '1px solid rgba(212, 175, 106, 0.15)'
            }}>
              <div style={{ fontWeight: 600, color: '#d4af6a', marginBottom: '8px' }}>T/F 维度：{mbtiType[2]}</div>
              <ul style={{ marginLeft: '20px', marginTop: '6px', lineHeight: '1.6' }}>
                {reasoning.tf.map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
            </div>
            <div style={{ 
              padding: '12px 16px',
              background: 'rgba(255, 255, 255, 0.6)',
              borderRadius: '12px',
              marginBottom: '12px',
              border: '1px solid rgba(212, 175, 106, 0.15)'
            }}>
              <div style={{ fontWeight: 600, color: '#667eea', marginBottom: '8px' }}>J/P 维度：{mbtiType[3]}</div>
              <ul style={{ marginLeft: '20px', marginTop: '6px', lineHeight: '1.6' }}>
                {reasoning.jp.map((reason, index) => (
                  <li key={index}>{reason}</li>
                ))}
              </ul>
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#888' }}>倾向：<span style={{ color: '#667eea' }}>{trends.jp}</span></div>
            </div>
          </div>
        </div>
        {hasConflict && (
          <div style={{ 
            marginTop: '16px', 
            padding: '12px 16px',
            fontSize: '13px', 
            color: '#d32f2f',
            background: 'rgba(211, 47, 47, 0.08)',
            borderRadius: '12px',
            border: '1px solid rgba(211, 47, 47, 0.2)'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '8px' }}>⚠️ 地支关系：</div>
            {conflicts.length > 0 && (
              <div>地支冲：{conflicts.map(([b1, b2]) => `${b1}${b2}`).join('、')}</div>
            )}
            {punishments.length > 0 && (
              <div>地支刑：{punishments.map(([b1, b2]) => `${b1}${b2}`).join('、')}</div>
            )}
          </div>
        )}
      </div> */}
    </div>
  );
};

export default FourPillars;
